import {
  Search,
  Filter,
  ArrowUpDown,
  SortAsc,
  SortDesc,
  X,
  Layers,
} from 'lucide-react';
import { useState } from 'react';
import type { BuildTag, ProjectCategory } from '../../types/buildTeam';
import { PROJECT_CATEGORIES } from '../../types/buildTeam';
import { cn } from '../../utils/cn';
import { useIsMobile } from '../../hooks/useIsMobile';

function formatStatusLabel(s: string) {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatCategoryLabel(c: ProjectCategory) {
  return c
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface BuildFiltersProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  categoryFilter: string;
  onCategoryChange: (v: string) => void;
  tags: BuildTag[];
  tagFilter: string;
  onTagChange: (v: string) => void;
  priorityMin: string;
  onPriorityMinChange: (v: string) => void;
  priorityMax: string;
  onPriorityMaxChange: (v: string) => void;
  sortBy: string;
  onSortByChange: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrderChange: (v: 'asc' | 'desc') => void;
  statuses: string[];
}

const ICON_CLASS = 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none';
const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export const BuildFilters = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  categoryFilter,
  onCategoryChange,
  tags,
  tagFilter,
  onTagChange,
  priorityMin,
  onPriorityMinChange,
  priorityMax,
  onPriorityMaxChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  statuses,
}: BuildFiltersProps) => {
  const isMobile = useIsMobile();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const hasActiveFilters =
    statusFilter || categoryFilter || tagFilter || priorityMin || priorityMax;

  const clearFilters = () => {
    onStatusChange('');
    onCategoryChange('');
    onTagChange('');
    onPriorityMinChange('');
    onPriorityMaxChange('');
    onSearchChange('');
  };

  return (
    <div className="space-y-2">
      <div className={cn('flex gap-2', isMobile ? 'flex-col' : 'flex-row items-center')}>
        <div className="relative flex-1 min-w-0">
          <Search className={ICON_CLASS} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="glass-input w-full pl-9"
            placeholder="Search projects, builds…"
          />
        </div>

        <div className={cn('relative', !isMobile && 'w-40')}>
          <Filter className={ICON_CLASS} />
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            className="glass-select w-full pl-9"
            aria-label="Status"
          >
            <option value="">All Statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {formatStatusLabel(s)}
              </option>
            ))}
          </select>
        </div>

        <div className={cn('relative', !isMobile && 'w-44')}>
          <Layers className={ICON_CLASS} />
          <select
            value={categoryFilter}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="glass-select w-full pl-9"
            aria-label="Category"
          >
            <option value="">All Categories</option>
            {PROJECT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {formatCategoryLabel(c)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <div className={cn('relative', isMobile ? 'flex-1' : 'w-44')}>
            <ArrowUpDown className={ICON_CLASS} />
            <select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              className="glass-select w-full pl-9"
              aria-label="Sort by"
            >
              <option value="created_at">Date Created</option>
              <option value="updated_at">Last Updated</option>
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="due_date">Due Date</option>
              <option value="title">Title</option>
            </select>
          </div>
          <button
            onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-icon border-glass-200/10"
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            aria-label={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
            type="button"
          >
            {sortOrder === 'asc' ? (
              <SortAsc className="w-4 h-4" />
            ) : (
              <SortDesc className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={cn(
            'text-xs font-medium transition-colors',
            showAdvanced ? 'text-accent-300' : 'text-glass-400 hover:text-white'
          )}
        >
          {showAdvanced ? 'Hide filters' : 'More filters'}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-medium text-red-400 hover:text-red-300 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className={cn('grid gap-3 pt-1', isMobile ? 'grid-cols-1' : 'grid-cols-3')}>
          <div>
            <label className={LABEL_CLASS}>Tag</label>
            <select
              value={tagFilter}
              onChange={(e) => onTagChange(e.target.value)}
              className="glass-select w-full"
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>Min Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={priorityMin}
              onChange={(e) => onPriorityMinChange(e.target.value)}
              className="glass-input w-full tabular-nums"
              placeholder="1"
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Max Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={priorityMax}
              onChange={(e) => onPriorityMaxChange(e.target.value)}
              className="glass-input w-full tabular-nums"
              placeholder="10"
            />
          </div>
        </div>
      )}
    </div>
  );
};
